import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { getAdminDashboard } from '../../../api/redux/slices/Admin';
import { LoaderComponent } from '../../../helpers/components/loader';
import { roundToThreeDecimals } from '../../../helpers/modules';
import {
    BsBuilding, BsPeople, BsBox, BsGrid, BsCart,
    BsGraphUp, BsWallet, BsClock, BsAward,
    BsCash, BsCreditCard, BsBank, BsClockHistory
} from 'react-icons/bs';
import './dashboard.scss';

const Dashboard = () => {
    const dispatch = useDispatch();
    const token = localStorage.getItem('accesstoken');
    const [dashboardData, setDashboardData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (token) {
            dispatch(getAdminDashboard({ token }))
                .unwrap()
                .then((res) => setDashboardData(res.data))
                .catch((err) => console.error('Error fetching dashboard:', err))
                .finally(() => setLoading(false));
        }
    }, [dispatch, token]);

    if (loading) return <LoaderComponent />;

    const dashboardCompany   = dashboardData?.company            || {};
    const dashboardStats     = dashboardData?.stats              || {};
    const dashboardPayments  = dashboardData?.paymentDistribution || {};
    const dashboardOrders    = dashboardData?.recentOrders       || [];
    const chartData          = dashboardData?.monthlyStats       || [];

    // Safe max — never 0 to avoid division by zero
    const maxOrdersAmount = Math.max(...chartData.map(d => d.ordersAmount), 1);
    const maxNewClients   = Math.max(...chartData.map(d => d.newClients),   1);

    const totalNewClients12   = chartData.reduce((s, d) => s + d.newClients,   0);
    const totalOrdersAmount12 = chartData.reduce((s, d) => s + d.ordersAmount, 0);

    return (
        <div className="dashboard-container">

            {/* ── Header ── */}
            <div className="dashboard-header">
                <h1>Tableau de bord</h1>
                <p className="company-name">
                    <BsBuilding />
                    {dashboardCompany?.name || 'Votre société'}
                </p>
            </div>

            {/* ── Stat cards ── */}
            <div className="stats-grid">
                {[
                    { icon: <BsPeople />,   cls: 'clients',   value: dashboardStats.clientsCount   || 0,                                          label: 'Clients'          },
                    { icon: <BsBox />,      cls: 'products',  value: dashboardStats.productsCount  || 0,                                          label: 'Produits'         },
                    { icon: <BsGrid />,     cls: 'categories',value: dashboardStats.categoriesCount|| 0,                                          label: 'Catégories'       },
                    { icon: <BsCart />,     cls: 'orders',    value: dashboardStats.totalOrders    || 0,                                          label: 'Commandes'        },
                    { icon: <BsGraphUp />,  cls: 'revenue',   value: `${roundToThreeDecimals(dashboardStats.totalOrderAmount  || 0)} DT`,         label: "Chiffre d'affaires" },
                    { icon: <BsWallet />,   cls: 'paid',      value: `${roundToThreeDecimals(dashboardStats.totalPaid         || 0)} DT`,         label: 'Total payé'       },
                    { icon: <BsClock />,    cls: 'remaining', value: `${roundToThreeDecimals(dashboardStats.remainingAmount   || 0)} DT`,         label: 'Reste à payer', isRed: true },
                    { icon: <BsAward />,    cls: 'sponsor',   value: `${roundToThreeDecimals(dashboardCompany.sponsorTotal || 0)} DT`,                                          label: 'Parrainages'      },
                ].map(({ icon, cls, value, label, isRed }) => (
                    <div className="stat-card" key={cls}>
                        <div className={`stat-icon ${cls}`}>{icon}</div>
                        <div className="stat-info">
                            <h3 className={isRed ? 'remaining-amount' : ''}>{value}</h3>
                            <p>{label}</p>
                        </div>
                    </div>
                ))}
            </div>

            {/* ── Monthly chart ── */}
            <div className="chart-section">
                <div className="section-header">
                    <h2>Évolution mensuelle</h2>
                    <div className="legend">
                        <span className="legend-bar orders-legend"></span>
                        <span>Montant des commandes (DT)</span>
                        <span
                            className="legend-bar clients-legend"
                            style={{ backgroundColor: '#FF9800', marginLeft: '1rem' }}
                        ></span>
                        <span>Nouveaux clients</span>
                    </div>
                </div>

                <div className="chart-container">
                    {chartData.length === 0 ? (
                        <div className="no-chart-data">
                            <p>Aucune donnée disponible pour les 12 derniers mois</p>
                            <p>Les statistiques apparaîtront après vos premières commandes</p>
                        </div>
                    ) : (
                        <div className="chart-bars">
                            {chartData.map((item, index) => {
                                const ordersHeight  = maxOrdersAmount > 0
                                    ? (item.ordersAmount / maxOrdersAmount) * 100
                                    : 0;
                                const clientsHeight = maxNewClients > 0
                                    ? (item.newClients / maxNewClients) * 100
                                    : 0;

                                return (
                                    <div key={index} className="chart-column">
                                        <div className="bars-group">

                                            {item.newClients > 0 ? (
                                                <div
                                                    className="chart-bar clients-bar"
                                                    style={{
                                                        height: `${clientsHeight}%`,
                                                        backgroundColor: '#FF9800',
                                                        minHeight: '4px'   
                                                    }}
                                                >
                                                    <span className="tooltip">
                                                        {item.newClients} nouveau(x) client(s)
                                                    </span>
                                                </div>
                                            ) : (
                                                <div className="chart-bar clients-bar" style={{ height: 0 }} />
                                            )}

                                            {item.ordersAmount > 0 ? (
                                                <div
                                                    className="chart-bar orders-bar"
                                                    style={{
                                                        height: `${ordersHeight}%`,
                                                        backgroundColor: '#2E7D32',
                                                        minHeight: '4px'
                                                    }}
                                                >
                                                    <span className="tooltip">
                                                        {roundToThreeDecimals(item.ordersAmount)} DT
                                                    </span>
                                                </div>
                                            ) : (
                                                <div className="chart-bar orders-bar" style={{ height: 0 }} />
                                            )}

                                        </div>
                                        <div className="chart-label">{item.month}</div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {chartData.length > 0 && (
                    <div className="chart-summary">
                        <div className="summary-item">
                            <span className="summary-label">Nouveaux clients (12 mois)</span>
                            <span className="summary-value">{totalNewClients12}</span>
                        </div>
                        <div className="summary-item">
                            <span className="summary-label">Montant total (12 mois)</span>
                            <span className="summary-value">
                                {roundToThreeDecimals(totalOrdersAmount12)} DT
                            </span>
                        </div>
                    </div>
                )}
            </div>

            {/* ── Payment distribution + Recent orders ── */}
            <div className="two-columns">

                <div className="payment-section">
                    <div className="section-header">
                        <h2>Distribution des paiements</h2>
                    </div>
                    <div className="payment-stats">
                        {[
                            { icon: <BsCash />,       cls: 'cash',     label: 'Espèces',          key: 'cash',        color: '#4CAF50' },
                            { icon: <BsCreditCard />, cls: 'cheque',   label: 'Chèque',           key: 'cheque',      color: '#2196F3' },
                            { icon: <BsBank />,       cls: 'transfer', label: 'Virement bancaire', key: 'bankTransfer',color: '#FF9800' },
                        ].map(({ icon, cls, label, key, color }) => {
                            const amount = dashboardPayments[key] || 0;
                            const pct    = dashboardStats.totalPaid
                                ? (amount / dashboardStats.totalPaid) * 100
                                : 0;
                            return (
                                <div className={`payment-item ${cls}`} key={key}>
                                    <div className="payment-icon">{icon}</div>
                                    <div className="payment-info">
                                        <span className="payment-label">{label}</span>
                                        <span className="payment-amount">
                                            {roundToThreeDecimals(amount)} DT
                                        </span>
                                    </div>
                                    <div className="payment-bar">
                                        <div
                                            className="bar-fill"
                                            style={{ width: `${pct}%`, backgroundColor: color }}
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div className="recent-orders">
                    <div className="section-header">
                        <h2>Commandes récentes</h2>
                        <BsClockHistory className="history-icon" />
                    </div>
                    <div className="orders-list">
                        {dashboardOrders.length > 0 ? (
                            dashboardOrders.map((order, index) => (
                                <div key={index} className="order-item">
                                    <div className="order-info">
                                        <span className="order-ref">{order.reference}</span>
                                        <span className="order-date">
                                            {new Date(order.date).toLocaleDateString('fr-FR')}
                                        </span>
                                    </div>
                                    <div className="order-status">
                                        <span className={`status-badge ${order.status === 'ACCEPTED' ? 'accepted' : 'pending'}`}>
                                            {order.status === 'ACCEPTED' ? 'Acceptée' : 'En cours'}
                                        </span>
                                        <span className="order-amount">
                                            {roundToThreeDecimals(order.total)} DT
                                        </span>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <p className="no-data">Aucune commande récente</p>
                        )}
                    </div>
                </div>

            </div>
        </div>
    );
};

export default Dashboard;